import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePreferences } from '../contexts/PreferencesContext';
import axios from 'axios';
import { 
  ArrowLeft, Brain, Trophy, Star, CheckCircle, XCircle, 
  ChevronRight, Lock, Award, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GrandQuiz = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const level = searchParams.get('level') || 'beginner';
  
  const { token } = useAuth();
  const { language } = useLanguage();
  const { scriptMode } = usePreferences();
  
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(true);
  const [canTakeQuiz, setCanTakeQuiz] = useState(null);
  const [quizInfo, setQuizInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkCanTakeQuiz();
  }, [level]);

  const checkCanTakeQuiz = async () => {
    try {
      const response = await axios.get(`${API}/user/can-take-grand-quiz/${level}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCanTakeQuiz(response.data);
      
      if (response.data.can_take_grand_quiz) {
        fetchGrandQuiz();
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to check grand quiz eligibility:', error);
      // Allow taking quiz anyway if check fails
      fetchGrandQuiz();
    }
  };

  const fetchGrandQuiz = async () => {
    try {
      // Pass language parameter for strict language filtering
      const langParam = language === 'en' ? 'en' : 'fr';
      const response = await axios.get(`${API}/quiz/grand/${level}?lang=${langParam}`);
      
      if (response.data.questions && response.data.questions.length > 0) {
        setQuestions(response.data.questions);
        setQuizInfo(response.data);
        setError(null);
      } else {
        setError({
          type: 'no_questions',
          message: language === 'en' 
            ? 'Not enough content for Grand Quiz' 
            : 'Pas assez de contenu pour le Grand Quiz'
        });
      }
    } catch (error) {
      console.error('Failed to fetch grand quiz:', error);
      setError({
        type: 'fetch_error',
        message: language === 'en' 
          ? 'Error loading Grand Quiz' 
          : 'Erreur de chargement du Grand Quiz'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (answer, expectedCorrectAnswer) => {
    if (isAnswered) return;
    
    setSelectedAnswer(answer);
    setIsAnswered(true);
    
    if (answer === expectedCorrectAnswer) {
      setScore(score + 1);
    }
  };

  const nextQuestion = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(null);
      setIsAnswered(false);
    } else {
      submitQuiz();
    }
  };

  const submitQuiz = async () => {
    try {
      const response = await axios.post(
        `${API}/quiz/grand/submit`,
        {
          score: score,
          total: questions.length,
          level: level
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowResults(true);
      
      if (response.data.next_level_unlocked) {
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error('Failed to submit grand quiz:', error);
      setShowResults(true);
    }
  };

  const restartQuiz = () => {
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setIsAnswered(false);
    setScore(0);
    setShowResults(false);
    fetchGrandQuiz();
  };

  const getLevelName = () => {
    const names = {
      beginner: language === 'en' ? 'Level 1 - Beginner' : 'Niveau 1 - Débutant',
      intermediate: language === 'en' ? 'Level 2 - Intermediate' : 'Niveau 2 - Intermédiaire',
      advanced: language === 'en' ? 'Level 3 - Advanced' : 'Niveau 3 - Avancé'
    };
    return names[level] || level;
  };

  // Show locked screen if can't take quiz
  if (!loading && canTakeQuiz && !canTakeQuiz.can_take_grand_quiz) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center border-2 border-stone-200"
        >
          <div className="w-20 h-20 bg-stone-100 rounded-full mx-auto mb-6 flex items-center justify-center">
            <Lock className="w-10 h-10 text-stone-400" />
          </div>
          <h2 className="font-playfair text-2xl font-bold text-charcoal mb-4">
            {language === 'en' ? 'Grand Quiz Locked' : 'Grand Quiz Verrouillé'}
          </h2>
          <p className="font-nunito text-charcoal/70 mb-4">
            {canTakeQuiz.message}
          </p>
          <div className="bg-sand rounded-xl p-4 mb-6">
            <p className="font-nunito text-sm text-charcoal/60 mb-2">
              {language === 'en' ? 'Completed categories:' : 'Catégories complétées :'}
            </p>
            <p className="font-nunito font-bold text-primary text-2xl">
              {canTakeQuiz.completed_categories?.length || 0} / {canTakeQuiz.required_categories}
            </p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="tile-button bg-primary text-white font-nunito font-bold py-3 px-6 rounded-xl shadow-[0_4px_0_#9a360b] flex items-center justify-center gap-2 mx-auto"
          >
            <ArrowLeft className="w-5 h-5" />
            {language === 'en' ? 'Back to Level' : 'Retour au Niveau'}
          </button>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="text-center">
          <Award className="w-16 h-16 text-primary animate-bounce mx-auto mb-4" />
          <p className="font-nunito text-xl text-charcoal">
            {language === 'en' ? 'Preparing Grand Quiz...' : 'Préparation du Grand Quiz...'}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center border-2 border-stone-200"
        >
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="font-playfair text-2xl font-bold text-charcoal mb-4">
            {language === 'en' ? 'Error' : 'Erreur'}
          </h2>
          <p className="font-nunito text-charcoal/70 mb-6">{error.message}</p>
          <button
            onClick={() => navigate(-1)}
            className="tile-button bg-primary text-white font-nunito font-bold py-3 px-6 rounded-xl shadow-[0_4px_0_#9a360b] flex items-center justify-center gap-2 mx-auto"
          >
            <ArrowLeft className="w-5 h-5" />
            {language === 'en' ? 'Go Back' : 'Retour'}
          </button>
        </motion.div>
      </div>
    );
  }

  if (showResults) {
    const percentage = Math.round((score / questions.length) * 100);
    const passed = percentage >= 80;

    return (
      <div className="min-h-screen bg-sand flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: 'spring' }}
            className={`w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center ${
              passed ? 'bg-gradient-to-br from-green-400 to-green-600' : 'bg-orange-100'
            }`}
          >
            {passed ? (
              <Trophy className="w-12 h-12 text-white" />
            ) : (
              <Star className="w-12 h-12 text-orange-500" />
            )}
          </motion.div>

          <h2 className="font-playfair text-3xl font-bold text-charcoal mb-2">
            {passed 
              ? (language === 'en' ? '🎉 Congratulations!' : '🎉 Félicitations !') 
              : (language === 'en' ? 'Good effort!' : 'Bon effort !')}
          </h2>

          <p className="font-nunito text-lg text-charcoal/70 mb-2">
            Grand Quiz - {getLevelName()}
          </p>

          <p className="font-nunito text-lg text-charcoal/70 mb-6">
            {language === 'en' 
              ? `You scored ${score} out of ${questions.length}` 
              : `Vous avez obtenu ${score} sur ${questions.length}`}
          </p>

          <div className={`text-6xl font-bold mb-6 ${passed ? 'text-green-600' : 'text-orange-500'}`}>
            {percentage}%
          </div>

          {passed ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 mb-6"
            >
              <div className="flex items-center justify-center gap-2 text-green-700 mb-2">
                <Sparkles className="w-5 h-5" />
                <span className="font-nunito font-bold">
                  {language === 'en' ? 'Level Completed!' : 'Niveau Terminé !'}
                </span>
              </div>
              {level !== 'advanced' && (
                <p className="font-nunito text-sm text-green-600">
                  {language === 'en' 
                    ? 'Next level unlocked!' 
                    : 'Niveau suivant débloqué !'}
                </p>
              )}
            </motion.div>
          ) : (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
              <p className="font-nunito text-sm text-orange-700">
                {language === 'en' 
                  ? '80% required to pass. Keep practicing!' 
                  : '80% requis pour réussir. Continuez à pratiquer !'}
              </p>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={restartQuiz}
              className="w-full tile-button bg-primary text-white font-nunito font-bold py-3 px-6 rounded-xl shadow-[0_4px_0_#9a360b] flex items-center justify-center gap-2"
            >
              <Brain className="w-5 h-5" />
              {language === 'en' ? 'Try Again' : 'Réessayer'}
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full tile-button bg-stone-200 text-charcoal font-nunito font-bold py-3 px-6 rounded-xl flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              {language === 'en' ? 'Back to Dashboard' : 'Retour au Tableau de bord'}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex] || {};
  const questionType = currentQuestion?.type || 'mcq';
  
  // For fill_blank (grammar), always use phonetic. For MCQ, use scriptMode
  const useArabicScript = scriptMode === 'arabic' && questionType !== 'fill_blank';
  
  // Get options and correct answer based on script mode
  const questionOptions = useArabicScript 
    ? (currentQuestion?.options_arabic || currentQuestion?.options || [])
    : (currentQuestion?.options_phonetic || currentQuestion?.options || []);
  
  const correctAnswer = useArabicScript
    ? (currentQuestion?.correct_answer_arabic || currentQuestion?.correct_answer)
    : (currentQuestion?.correct_answer_phonetic || currentQuestion?.correct_answer);
  
  const isCorrect = selectedAnswer === correctAnswer;

  if (!currentQuestion || !questionOptions.length) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="text-center">
          <Award className="w-16 h-16 text-primary animate-bounce mx-auto mb-4" />
          <p className="font-nunito text-xl text-charcoal">
            {language === 'en' ? 'Loading question...' : 'Chargement...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sand to-amber-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-primary/80 text-white shadow-lg">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-white/80 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-nunito">{language === 'en' ? 'Exit' : 'Quitter'}</span>
            </button>
            
            <div className="text-center">
              <h1 className="font-playfair text-xl font-bold flex items-center gap-2">
                <Award className="w-6 h-6" />
                Grand Quiz
              </h1>
              <p className="font-nunito text-sm text-white/80">{getLevelName()}</p>
            </div>

            <div className="font-nunito font-bold">
              {currentIndex + 1} / {questions.length}
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-3 h-3 bg-white/20 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-white"
              initial={{ width: 0 }}
              animate={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-8 max-w-2xl">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border-2 border-stone-200"
          >
            {/* Category badge */}
            <div className="flex justify-center mb-4">
              <span className="bg-accent/10 text-accent px-3 py-1 rounded-full text-sm font-nunito">
                {currentQuestion.category}
              </span>
            </div>

            {/* Question */}
            <div className="text-center mb-8">
              <p className="font-nunito text-charcoal/70 mb-6">
                {language === 'en' ? currentQuestion.question_text_en : currentQuestion.question_text}
              </p>
              
              {/* For fill_blank questions, show the sentence with blank */}
              {questionType === 'fill_blank' && currentQuestion.blank_sentence && (
                <div className="bg-secondary/10 rounded-xl p-4 mb-4">
                  <p className="font-playfair text-2xl sm:text-3xl font-bold text-secondary">
                    {currentQuestion.blank_sentence.replace('___', '______')}
                  </p>
                  <p className="font-nunito text-sm text-charcoal/60 mt-2">
                    {language === 'en' ? 'Fill in the blank' : 'Complétez le trou'}
                  </p>
                </div>
              )}

              {/* Image if available */}
              {currentQuestion.image_url && (
                <div className="flex justify-center mb-4">
                  <img
                    src={`${BACKEND_URL}${currentQuestion.image_url}`}
                    alt="Quiz"
                    className="w-40 h-40 object-contain bg-stone-100 rounded-xl border-2 border-stone-200 shadow-md"
                  />
                </div>
              )}
            </div>

            {/* Options */}
            <div className="grid gap-3" data-testid="grand-quiz-options-container">
              {questionOptions.map((option, index) => {
                let buttonClass = 'bg-stone-50 border-stone-200 hover:border-primary hover:bg-primary/5';
                
                if (isAnswered) {
                  if (option === correctAnswer) {
                    buttonClass = 'bg-green-100 border-green-500 text-green-800';
                  } else if (option === selectedAnswer && !isCorrect) {
                    buttonClass = 'bg-red-100 border-red-500 text-red-800';
                  }
                } else if (option === selectedAnswer) {
                  buttonClass = 'bg-primary/10 border-primary';
                }

                return (
                  <motion.button
                    key={index}
                    data-testid={`grand-quiz-option-${index}`}
                    whileHover={!isAnswered ? { scale: 1.02 } : {}}
                    whileTap={!isAnswered ? { scale: 0.98 } : {}}
                    onClick={() => handleAnswer(option, correctAnswer)}
                    disabled={isAnswered}
                    className={`w-full p-4 rounded-xl border-2 ${useArabicScript ? 'font-amiri' : 'font-nunito'} text-lg text-left transition-all ${buttonClass}`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{option}</span>
                      {isAnswered && option === correctAnswer && (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      )}
                      {isAnswered && option === selectedAnswer && !isCorrect && (
                        <XCircle className="w-6 h-6 text-red-600" />
                      )}
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {/* Feedback and Next */}
            {isAnswered && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6"
              >
                <div className={`p-4 rounded-xl mb-4 ${isCorrect ? 'bg-green-100' : 'bg-red-100'}`}>
                  <p className={`font-nunito font-bold ${isCorrect ? 'text-green-800' : 'text-red-800'}`}>
                    {isCorrect 
                      ? (language === 'en' ? '✓ Correct!' : '✓ Correct !') 
                      : (language === 'en' 
                        ? `✗ The answer was: ${correctAnswer}` 
                        : `✗ La réponse était : ${correctAnswer}`)}
                  </p>
                </div>

                <button
                  onClick={nextQuestion}
                  data-testid="grand-quiz-next-btn"
                  className="w-full tile-button bg-primary text-white font-nunito font-bold py-4 px-6 rounded-xl shadow-[0_4px_0_#9a360b] flex items-center justify-center gap-2"
                >
                  {currentIndex < questions.length - 1 
                    ? (language === 'en' ? 'Next Question' : 'Question Suivante')
                    : (language === 'en' ? 'See Results' : 'Voir les Résultats')}
                  <ChevronRight className="w-5 h-5" />
                </button>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Score indicator */}
        <div className="mt-6 text-center">
          <p className="font-nunito text-charcoal/60">
            {language === 'en' ? 'Current score:' : 'Score actuel :'} 
            <span className="font-bold text-primary ml-1">{score}/{currentIndex + (isAnswered ? 1 : 0)}</span>
            <span className="text-charcoal/40 ml-2">
              ({Math.round((score / (currentIndex + (isAnswered ? 1 : 0) || 1)) * 100)}%)
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default GrandQuiz;
